import { createSelector } from "@reduxjs/toolkit";
import { IRootState } from "../../../../../store";
import { mcBimMongoRTK } from "../../../admin/mcbim/McBimMongoRTK";
import { selectConnectedBimSpacesDisabled } from "../../issueSelectors";
import { selectProjectNo } from "../../../../projectSelectors";
import { openapi } from "../../../../../../openapi";

export const selectMcbimIsActivated = createSelector(
  [(state: IRootState) => state.adminReducer.project],
  (project) => project?.mcbim?.activated === true,
);

export const selectMcbimMongoFilesStatsArgs = createSelector(
  [(state: IRootState) => state.adminReducer.project, selectMcbimIsActivated],
  (project, mcbimIsActivated): { projectId: string } | null => {
    if (!mcbimIsActivated || project?._id == null) return null;

    return { projectId: project._id };
  },
);

export const selectMcbimMongoFilesStatsQuery = createSelector(
  [(state: IRootState) => state, selectMcbimMongoFilesStatsArgs],
  (state, args) => {
    if (args == null) {
      return {
        data: [],
        hasLoaded: false,
        isFetching: false,
        isError: false,
        error: null,
      };
    }

    const q = mcBimMongoRTK.endpoints.getMcBimMongoFilesStats.select(args)(state);

    return {
      data: q.data ?? [],
      hasLoaded: q.status === "fulfilled",
      isFetching: q.status === "pending",
      isError: q.status === "rejected",
      error: q.error ?? null,
    };
  },
);

export const selectMcbimHasNoSyncedFiles = createSelector(
  [selectMcbimIsActivated, selectMcbimMongoFilesStatsQuery],
  (mcbimIsActivated, filesQuery) =>
    mcbimIsActivated && filesQuery.hasLoaded && !filesQuery.isError && filesQuery.data.length === 0,
);

const selectMcbimHasLoadedFiles = createSelector(
  [selectMcbimIsActivated, selectMcbimMongoFilesStatsQuery],
  (mcbimIsActivated, filesQuery) =>
    mcbimIsActivated && filesQuery.hasLoaded && !filesQuery.isError && filesQuery.data.length > 0,
);

export const selectMcbimSpacesQuery = createSelector(
  [(state: IRootState) => state, selectProjectNo],
  (state, projectNo) => {
    if (projectNo == null) {
      return {
        data: [],
        hasLoaded: false,
        isFetching: false,
        isError: false,
        error: null,
      };
    }

    const q = openapi.endpoints.getMcbimSpaces.select({ projectNo })(state);

    return {
      data: q.data ?? [],
      hasLoaded: q.status === "fulfilled",
      isFetching: q.status === "pending",
      isError: q.status === "rejected",
      error: q.error ?? null,
    };
  },
);

export const selectMcbimHasNoSpaces = createSelector(
  [selectMcbimHasLoadedFiles, selectMcbimSpacesQuery],
  (mcbimHasLoadedFiles, spacesQuery) =>
    mcbimHasLoadedFiles && spacesQuery.hasLoaded && !spacesQuery.isError && spacesQuery.data.length === 0,
);
